import React, { useEffect, useState } from "react";
import { apiRequestWithAuth } from "../../utils/api";
import Alert from "../../components/ui/alert/Alert";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import FileInput from "../../components/form/input/FileInput";
import {Textarea} from "@mantine/core";
import {useNavigate, useParams} from "react-router-dom";
import MyEditor from "../../components/form/input/MyEditor";

type Category = {
    id: number;
    title: string;
};

const TeammateEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const defaultForm = {
        title: "",
        shortDescription: "",
        postContent: "",
        categoryId: "",
        status: 1,
        image: null,
        type: 4,
        orderNo: "",
        metaTitle: "",
        metaKeyword: "",
        metaDescription: "",
    };

    const [categories, setCategories] = useState<Category[]>([]);
    const [formData, setFormData] = useState(defaultForm);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const navigate = useNavigate();

    // Fetch existing course
    useEffect(() => {
        const fetchPost = async () => {
            try {
                const res = await apiRequestWithAuth(`/posts/${id}`, "GET", {});
                const data = res.result;

                setFormData({
                    title: data.title,
                    shortDescription: data.shortDescription,
                    postContent: data.content,
                    orderNo: data.orderNo,
                    categoryId: data.categoryId,
                    status: data.status,
                    image: data.image,
                    type: data.type,
                    metaTitle: data.metaTitle,
                    metaKeyword: data.metaKeyword,
                    metaDescription: data.metaDescription,
                });

                // Lấy ảnh đầu tiên nếu có
                const file = data.files?.find((f: any) => f.metaKey === "image");
                if (file && file.publicPath) {
                    setPreview(file.publicPath);
                }
            } catch (error) {
                console.error("Lỗi khi lấy dữ liệu slider:", error);
            }
        };

        if (id) fetchPost();
    }, [id]);

    const fetchCategories = async () => {
        try {
            const res = await apiRequestWithAuth("/category", "GET", {});
            setCategories(res.result);
        } catch (error) {
            console.error("Lỗi lấy danh mục:", error);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = "metaDescription";
        const value = e.target ? e.target.value : "";
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = "shortDescription";
        const value = e.target ? e.target.value : "";
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleUploadImage = async (file: File) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("subject", "post_cover");

        try {
            const res = await apiRequestWithAuth('/media/file', 'POST', formData);
            return res.result?.id || null;
        } catch (error) {
            console.error("Upload image failed", error);
            return null;
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setPreview(URL.createObjectURL(file));
            const uploadedId = await handleUploadImage(file);
            setFormData(prev => ({
                ...prev,
                files: [
                    { key: "image", fileId: uploadedId }
                ]
            }));
        }
    };

    const handleSave = async () => {
        try {
            await apiRequestWithAuth(`/posts/${id}`, "PUT", formData);
            setOpenSnackbar(true);
            setFormData(defaultForm);
            setSelectedFile(null);
            setPreview(null);
            setTimeout(() => navigate("/teammate"), 1000);
        } catch (error) {
            console.error("Update post failed", error);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    return (
        <div>
            {openSnackbar && (
                <Alert
                    variant="success"
                    title="Tạo bài viết thành công"
                    message="Bạn đã tạo bài viết mới thành công."
                />
            )}

            <PageMeta title="Tạo bài viết" description="Trang tạo mới bài viết" />
            <PageBreadcrumb pageTitle="Tạo bài viết" />

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
                <div className="space-y-6">
                    <ComponentCard title="Thông tin bài viết">
                        <div className="space-y-6">
                            <div>
                                <Label>Tiêu đề</Label>
                                <Input name="title" value={formData.title} onChange={handleChange}/>
                            </div>

                            <div>
                                <Label>Danh mục</Label>
                                <select
                                    name="categoryId"
                                    value={formData.categoryId}
                                    onChange={handleChange}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                >
                                    <option value="">-- Chọn danh mục --</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.title}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <Label>Mô tả ngắn</Label>
                                <Textarea name="shortDescription" value={formData.shortDescription}
                                          onChange={handleDescriptionChange}/>
                            </div>

                            <div>
                                <Label>Hình Ảnh</Label>
                                <FileInput onChange={handleFileChange}/>
                                {preview && (
                                    <img src={preview} alt="Preview"
                                         className="w-64 h-32 object-cover rounded border mt-2"/>
                                )}
                            </div>

                            <div>
                                <Label>Nội dung chi tiết</Label>
                                <MyEditor
                                    content={formData.postContent}
                                    onBlur={(value) => handleChange({target: {name: "postContent", value}} as any)}
                                />
                            </div>

                            <div>
                                <Label>Số Thứ Tự</Label>
                                <Input
                                    name="orderNo"
                                    type="number"
                                    value={formData.orderNo}
                                    onChange={handleChange}
                                />
                            </div>

                            <div>
                                <Label>Trạng thái</Label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                >
                                    <option value="1">Active</option>
                                    <option value="2">Draft</option>
                                    <option value="3">Inactive</option>
                                    <option value="4">Delete</option>
                                </select>
                            </div>
                        </div>
                    </ComponentCard>
                    <ComponentCard title="Thông tin seo">
                        <div>
                            <Label>Meta Title</Label>
                            <Input name="metaTitle" value={formData.metaTitle} onChange={handleChange}/>
                        </div>
                        <div>
                            <Label>Meta Keyword</Label>
                            <Input name="metaKeyword" value={formData.metaKeyword} onChange={handleChange}/>
                        </div>
                        <div>
                            <Label>Meta Description</Label>
                            <Textarea name="metaDescription" value={formData.metaDescription}
                                      onChange={handleTextareaChange}/>
                        </div>
                    </ComponentCard>
                </div>

                <button
                    onClick={handleSave}
                    className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300"
                >
                    Cập nhật bài viết
                </button>
            </div>
        </div>
    );
};

export default TeammateEditPage;
